import { useState } from "react";
import { useFetch } from "../../../common/hooks/useFetch.js";
import Swal from "sweetalert2";
import { saveFile } from "../../../common/utils/saveFile.util.js";
import { createSpeedManagmentService } from "../services/createSpeedManagment.service.js";
import { updateSpeedManagmentService } from "../services/updateSpeedManagment.service.js";
import { deleteSpeedManagmentService } from "../services/deleteSpeedManagment.service.js";
import { exportSpeedManagmentToExcelService } from "../services/exportSpeedManagmentToExcel.service.js";

const initialState = {
  date: "",
  ownInProgram: "",
  ownUsedMonth: "",
  contractorsInProgram: "",
  contractorsUsedMonth: "",
  thirdPartiesInProgram: "",
  thirdPartiesUsedMonth: "",
};

const isEmptyNumber = (value) =>
  value === "" || value === null || value === undefined;

export const useSpeedManagmentCommand = (refresh) => {
  const fetchData = useFetch();
  const [speedManagment, setSpeedManagment] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({ hasErrors: false, errors: {} });

  const handleChange = ({ name, value }) => {
    setSpeedManagment((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      hasErrors: false,
      errors: {
        ...prev.errors,
        [name]: "",
      },
    }));
  };

  const setDefaultValues = (defaultValues) => {
    if (!defaultValues) {
      setSpeedManagment(initialState);
      return;
    }
    const dateRaw = defaultValues.date || defaultValues.createdAt || "";
    const normalizedDate = dateRaw
      ? `${String(dateRaw).substring(0, 7)}-01`
      : "";
    setSpeedManagment({
      ...initialState,
      ...defaultValues,
      date: normalizedDate,
    });
  };

  const createSpeedManagment = async () => {
    try {
      const isValid = validations(speedManagment);
      if (!isValid) return;
      setLoading(true);
      const response =
        await createSpeedManagmentService(fetchData)(speedManagment);
      refresh?.();
      setSpeedManagment(initialState);
      Swal.fire({
        title: "Success",
        text: response.message,
        icon: "success",
      });
      return true;
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message,
        icon: "error",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async (filters) => {
    try {
      const response =
        await exportSpeedManagmentToExcelService(fetchData)(filters);
      if (response) {
        saveFile(response, "gestion_velocidad.xlsx");
      }
    } catch (error) {
      console.error("Error exporting data:", error);
    }
  };

  const updateSpeedManagment = async () => {
    try {
      const isValid = validations(speedManagment);
      if (!isValid) return;
      setLoading(true);
      const response =
        await updateSpeedManagmentService(fetchData)(speedManagment);
      refresh?.();
      setSpeedManagment(initialState);
      Swal.fire({
        title: "Éxito",
        text: response.message,
        icon: "success",
      });
      return true;
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteSpeedManagment = async (id) => {
    try {
      const isSure = await Swal.fire({
        title: "¿Estás seguro?",
        text: "¿Deseas eliminar este registro de gestión de velocidad?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!isSure.isConfirmed) return;

      setLoading(true);
      const response = await deleteSpeedManagmentService(fetchData)(id);
      refresh?.();
      Swal.fire({
        title: "Eliminado",
        text: response.message,
        icon: "success",
      });
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const validations = (data) => {
    const newErrors = {};

    if (!data.date) {
      newErrors.date = "El mes es obligatorio";
    }

    if (isEmptyNumber(data.ownInProgram)) {
      newErrors.ownInProgram =
        "El campo 'Vehículos propios en programa' es obligatorio";
    }

    if (isEmptyNumber(data.ownUsedMonth)) {
      newErrors.ownUsedMonth =
        "El campo 'Vehículos propios usados en el mes' es obligatorio";
    }

    if (isEmptyNumber(data.contractorsInProgram)) {
      newErrors.contractorsInProgram =
        "El campo 'Vehículos contratistas en programa' es obligatorio";
    }

    if (isEmptyNumber(data.contractorsUsedMonth)) {
      newErrors.contractorsUsedMonth =
        "El campo 'Vehículos contratistas usados en el mes' es obligatorio";
    }

    if (isEmptyNumber(data.thirdPartiesInProgram)) {
      newErrors.thirdPartiesInProgram =
        "El campo 'Vehículos de terceros en programa' es obligatorio";
    }

    if (isEmptyNumber(data.thirdPartiesUsedMonth)) {
      newErrors.thirdPartiesUsedMonth =
        "El campo 'Vehículos de terceros usados en el mes' es obligatorio";
    }

    const hasErrors = Object.keys(newErrors).length > 0;
    setErrors({ hasErrors, errors: newErrors });
    return !hasErrors;
  };

  return {
    speedManagment,
    loading,
    errors,
    handleChange,
    setDefaultValues,
    createSpeedManagment,
    updateSpeedManagment,
    deleteSpeedManagment,
    exportToExcel,
  };
};
